import axios, { type AxiosError } from 'axios';
import { HOST_API } from '../config';
import { getToken, notifyUnauthorized } from './token';
import { toApiError } from './apiError';
export type { ApiError } from './apiError';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** New workspace schemas use empty strings for editable, optional text. */
    preserveEmptyStrings?: boolean;
  }
}

// ----------------------------------------------------------------------

/** Shape of FastAPI's 422 validation payload. */
type FastApiValidationDetail = {
  loc: (string | number)[];
  msg: string;
};

type ApiErrorBody = {
  detail?: string | FastApiValidationDetail[] | { message: string; rows: string[] };
  message?: string;
};

const axiosInstance = axios.create({
  baseURL: HOST_API,
  // Any 2xx is a success — the portal uses 201 on create and 204 on delete.
  validateStatus: (status) => status >= 200 && status < 300,
});

/**
 * Flatten whatever the backend returned into a single message.
 * FastAPI sends `detail` as a string for HTTPException and as a list of
 * {loc, msg} objects for request-validation errors.
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody | string>) => {
    // An expired or revoked token signs the user out everywhere at once.
    if (error.response?.status === 401 && getToken()) {
      notifyUnauthorized();
    }
    return Promise.reject(toApiError(error));
  }
);

/**
 * Turn empty strings into null before sending, so the backend stores NULL
 * instead of ''. Mirrors the original app's request interceptor.
 */
function setNull<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => setNull(item)) as unknown as T;
  }

  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {
      ...(value as Record<string, unknown>),
    };

    Object.entries(out).forEach(([key, item]) => {
      if (typeof item === 'object' && item !== null) {
        out[key] = setNull(item);
      } else if (item === '') {
        out[key] = null;
      }
    });

    return out as T;
  }

  return value;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // FormData must go through untouched or the file boundary breaks.
    if (config.data && !(config.data instanceof FormData) && !config.preserveEmptyStrings) {
      config.data = setNull(config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default axiosInstance;
